<script setup lang="ts">
import { ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import { EMIT, GradeKey, GRADES_ORDER } from '@/enum';
import { PersonGrades } from '@/types/GradeInterface';
import { useGradeStore } from '@/store';

const props = defineProps<{
  visible: boolean;
  personId: number;
  organizationId: number;
  organizationName: string | null;
  currentGrades: PersonGrades[];
}>();

const emit = defineEmits(['update:visible', EMIT.CANCEL, EMIT.SYNC]);

const gradesStore = useGradeStore();

const visible = ref(props.visible);

// Sync internal visible with prop
watch(
  () => props.visible,
  (val) => {
    visible.value = val;
  },
);

// Emit update when internal visible changes
watch(visible, (val) => {
  emit('update:visible', val);
  if (!val) emit(EMIT.CANCEL);
});

const allGrades = GRADES_ORDER.map((gradeKey) => ({ grade: gradeKey }));

const selectedGrades = ref<{ grade: GradeKey }[]>([]);

// Sync selected grades when prop changes
watch(
  () => props.currentGrades,
  (newGrades) => {
    selectedGrades.value = allGrades.filter((g) => newGrades.some((ng) => ng.grade === g.grade));
  },
  { immediate: true },
);

const submit = async () => {
  const initial: GradeKey[] = props.currentGrades.map((g) => g.grade as GradeKey);
  const current: GradeKey[] = selectedGrades.value.map((g) => g.grade as GradeKey);

  const attached = current.filter((grade) => !initial.includes(grade));
  const detached = initial.filter((grade) => !current.includes(grade));

  if (attached.length === 0 && detached.length === 0) {
    gradesStore.doToast('No changes made to grades', '', true);
    visible.value = false;
    return;
  }

  if (detached.length > 0) {
    await gradesStore.detachPersonGrades({ grades: detached }, props.personId, props.organizationId);
    gradesStore.doToast('Grades detached successfully');
  }

  if (attached.length > 0) {
    await gradesStore.attachPersonGrades({ grades: attached }, props.personId, props.organizationId);
    gradesStore.doToast('Grades attached successfully');
  }
  emit(EMIT.SYNC);
  visible.value = false;
};

const close = () => {
  visible.value = false;
};
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    header="Edit Grades"
    :closable="true"
    :draggable="false"
    class="dialog__container"
  >
    <h2 v-if="props.organizationName">{{ props.organizationName }}</h2>
    <DataTable
      v-model:selection="selectedGrades"
      :value="allGrades"
      data-key="grade"
      selection-mode="multiple"
      responsive-layout="scroll"
      aria-label="Grades selection table"
    >
      <Column selection-mode="multiple" header-style="width:3rem" />
      <Column field="grade" header="Grade">
        <template #body="{ data }">{{ data.grade }}</template>
      </Column>
      <template #empty>No grades to display.</template>
    </DataTable>

    <template #footer>
      <AppButton text="Cancel" type="secondary" @click="close" />
      <AppButton text="Save" type="primary" @click="submit" />
    </template>
  </Dialog>
</template>
